import { useState, useEffect, useMemo } from 'react'
import { usePlates } from '../../hooks/usePlates'
import { SELECT_ARROW_STYLE, selectClasses } from './Select'

/** Sentinel for the "type it in" choice. Never stored — it only drives the UI. */
const OTHER = '__other__'

/**
 * Plates are stored uppercase, so a typed one is folded rather than leaving
 * "djmp544" and "DJMP544" to look like two different trucks on a report.
 */
export function normalisePlate(value) {
    return (value || '').toUpperCase()
}

/**
 * Pick a company plate, or type one that is not on the list.
 *
 * A native <select> with an "Other" escape hatch, rather than a text input backed
 * by a <datalist>. The datalist version read as a plain text box on a phone, gave
 * no hint that a list existed, and its suggestion popup is unreliable across
 * mobile browsers. A real <select> gets the full-screen picker every phone
 * already does well, and "Other" covers the rental or the truck bought since this
 * list was last edited.
 *
 * The list comes from the database and is edited on the Company Information
 * page, so adding a truck no longer needs a deploy. If it cannot be loaded the
 * picker still offers "Other", which is the whole entry path - just less
 * convenient.
 *
 * Pass `label` for a form field; leave it off inside a table cell, where the
 * surrounding markup provides its own spacing.
 */
export default function PlateField({
    value = '',
    onChange,
    label,
    error,
    required = false,
    disabled = false,
    placeholder = 'Select plate…',
    ...rest
}) {
    const { plates, loaded } = usePlates()
    const numbers = useMemo(() => plates.map(p => p.plate), [plates])

    const isKnown = numbers.includes(value)

    // Starts false and is decided by the effect below. Judging it on the first
    // render would put every plate in the text box, since the list has not
    // arrived yet.
    const [manual, setManual] = useState(false)

    // An old fuel line being edited may carry a plate that is not on the list -
    // a truck since sold, or one typed in as "Other". Switch to the text box so
    // it stays visible and editable, rather than the select silently reading as
    // nothing chosen. Only once the list is actually loaded: before that,
    // "unknown" just means "not here yet".
    useEffect(() => {
        if (loaded && value && !numbers.includes(value)) setManual(true)
    }, [value, loaded, numbers])

    function pick(next) {
        if (next === OTHER) {
            setManual(true)
            onChange('')          // clear, so the box starts empty to type into
        } else {
            setManual(false)
            onChange(next)
        }
    }

    // Inside a form the dropdown wears the same chrome as every other one on the
    // page. In a table cell it stays bare, like the inputs beside it.
    const chrome = label
        ? { className: selectClasses(error), style: SELECT_ARROW_STYLE }
        : {}

    const controls = (
        <>
            <select
                value={manual ? OTHER : value}
                onChange={e => pick(e.target.value)}
                disabled={disabled}
                {...chrome}
                {...rest}
            >
                <option value="">{placeholder}</option>

                {/* A plate that is not on the list - the list is still loading,
                    or the truck has since been sold - still has to read as the
                    one chosen rather than as nothing. */}
                {value && !isKnown && !manual && <option value={value}>{value}</option>}

                {plates.map(p => (
                    <option key={p.plate} value={p.plate}>
                        {p.description ? `${p.plate} — ${p.description}` : p.plate}
                    </option>
                ))}
                <option value={OTHER}>Other — type it in…</option>
            </select>

            {manual && (
                <input
                    type="text"
                    className="mt-2"
                    value={value}
                    onChange={e => onChange(normalisePlate(e.target.value))}
                    placeholder="Plate number"
                    autoComplete="off"
                    autoFocus
                    disabled={disabled}
                />
            )}
        </>
    )

    if (!label) return controls

    return (
        <div className="form-group">
            <label className="form-label">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {controls}
            {error && <p className="form-error">{error}</p>}
        </div>
    )
}
